/**
 * Browser Compatibility Detection
 * Checks for required browser features and warns users if unsupported
 */

export interface BrowserCapabilities {
  wasm: boolean;
  webWorkers: boolean;
  indexedDB: boolean;
  asyncIterator: boolean;
  bigInt: boolean;
  compatible: boolean;
  browser: string;
  warnings: string[];
}

export function detectBrowserCapabilities(): BrowserCapabilities {
  const warnings: string[] = [];

  // Detect browser
  const userAgent = navigator.userAgent;
  let browser = 'Unknown';

  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    browser = 'Chrome';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browser = 'Safari';
  } else if (userAgent.includes('Firefox')) {
    browser = 'Firefox';
  } else if (userAgent.includes('Edg')) {
    browser = 'Edge';
  }

  // Check WebAssembly support
  const hasWasm = (() => {
    try {
      if (typeof WebAssembly === 'object' &&
          typeof WebAssembly.instantiate === 'function') {
        const module = new WebAssembly.Module(
          Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00)
        );
        if (module instanceof WebAssembly.Module) {
          return new WebAssembly.Instance(module) instanceof WebAssembly.Instance;
        }
      }
    } catch (e) {
      // WASM not supported
    }
    return false;
  })();

  if (!hasWasm) {
    warnings.push('WebAssembly is not supported in your browser. WaySightAI requires WASM for forecasting.');
  }

  // Check Web Workers support
  const hasWebWorkers = typeof Worker !== 'undefined';
  if (!hasWebWorkers) {
    warnings.push('Web Workers are not supported. Large file parsing may be slow.');
  }

  // Check IndexedDB for potential future features
  const hasIndexedDB = typeof indexedDB !== 'undefined';
  if (!hasIndexedDB) {
    warnings.push('IndexedDB is not supported. Some features may be limited.');
  }

  // Check for async iterators (used by some libraries)
  const hasAsyncIterator = typeof Symbol !== 'undefined' && Symbol.asyncIterator !== undefined;

  // Check BigInt support (used by some WASM modules)
  const hasBigInt = typeof BigInt !== 'undefined';
  if (!hasBigInt) {
    warnings.push('BigInt is not supported. Some advanced features may not work.');
  }

  const compatible = hasWasm && hasWebWorkers;

  return {
    wasm: hasWasm,
    webWorkers: hasWebWorkers,
    indexedDB: hasIndexedDB,
    asyncIterator: hasAsyncIterator,
    bigInt: hasBigInt,
    compatible,
    browser,
    warnings,
  };
}

export function getBrowserRecommendation(): string {
  const capabilities = detectBrowserCapabilities();

  if (capabilities.compatible) {
    return `You're using ${capabilities.browser}. All features are supported!`;
  }

  return `Your browser (${capabilities.browser}) doesn't support all required features. Please use the latest version of Chrome, Firefox, Safari, or Edge for the best experience.`;
}

export function shouldShowCompatibilityWarning(): boolean {
  const capabilities = detectBrowserCapabilities();
  return !capabilities.compatible;
}
